<template>
  <div :class="$style.overlay" v-if="isVisible">
    <div :class="$style.notes">
      <b :class="$style.allNotesHeader">Notes</b>
      <div :class="$style.noteContainer">
        <div
          style="width: 100%"
          v-for="note of editableNotes"
          v-bind:key="note.id"
        >
          <div :class="$style.noteContent">
            <b :class="$style.noteHeader">{{ note.noteSubject }}</b>
            <div :class="$style.dateLine">
              {{ note.createdByUser.firstName }}
              {{ note.createdByUser.lastName }} - {{ note.noteType.name }} -
              {{ note.formattedDate }}
            </div>
            <div :class="$style.noteHeader">
              {{ note.note }}
            </div>
          </div>
          <hr :class="$style.dividerLine" />
        </div>
      </div>
      <div :class="$style.noteInputContainer">
        <div class="row" style="width: 100%">
          <div class="col-sm-3 text-center align-content-center">Subject</div>
          <div class="col-sm-9">
            <input
              type="text"
              placeholder="Subject..."
              @change="checkIfDisabled"
              v-model="newSubject"
            />
          </div>
        </div>
        <div class="row" style="width: 100%">
          <div class="col-sm-3 text-center align-content-center">Note</div>
          <div class="col-sm-9">
            <input
              type="text"
              placeholder="Write Note..."
              @change="checkIfDisabled"
              v-model="newNote"
            />
          </div>
        </div>
      </div>
      <div class="row" style="width: 100%">
        <div class="col-sm-12">
          <button
            @click="saveNewNote"
            :class="$style.saveWrapper"
            :disabled="saveDisabled"
          >
            <b :class="$style.save">Save</b>
          </button>
        </div>
      </div>
      <img
        @click="closeModal"
        :class="$style.icon"
        alt=""
        src="/static/img/grey-x.svg"
      />
    </div>
  </div>
</template>
<script>
import NotesAPIService from "src/servicehandlers/NotesAPIService";
import { useLoginStore } from "@/stores/loginStore";
import dayjs from "dayjs";

const notesAPIService = new NotesAPIService();
export default {
  name: "Notes",
  props: {
    isVisible: { type: Boolean, default: false },
    targetName: { type: String, required: false },
    targetUserId: { type: Number, default: null },
    filterTargetUserByNoteType: { type: Boolean, default: false },
    targetTaskId: { type: Number, default: null },
    targetEventId: { type: Number, default: null },
    noteTypeName: { type: String, default: "" },
    bottomRight: { type: Boolean, default: false },
  },
  data() {
    return {
      editableNotes: [],
      noteTypeOptions: [],
      noteType: null,
      currentUser: null,
      isCreatingNewNote: false,
      saveDisabled: true,
      newSubject: null,
      newNote: null,
    };
  },
  computed: {
    footerButtons() {
      return [
        {
          label: this.isCreatingNewNote ? "Save Note" : "Add Note",
          type: this.isCreatingNewNote ? "greenCheckButton" : undefined,
          onClick: this.isCreatingNewNote
            ? this.triggerFormSubmit
            : this.addNote,
        },
        { label: "Close", onClick: this.closeModal },
      ];
    },
  },
  async mounted() {
    this.loadUserId();
    if (this.isVisible) {
      await this.fetchNoteTypes();
      await this.fetchNotes();
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.fetchNoteTypes();
        this.fetchNotes();
      }
    },
  },
  methods: {
    loadUserId() {
      const store = useLoginStore();
      this.currentUser = store.getUser;
    },
    async fetchNotes() {
      try {
        let notes;
        if (
          this.targetUserId &&
          this.noteType &&
          this.filterTargetUserByNoteType
        ) {
          notes = await notesAPIService.getByTargetUserAndNoteTypeIds(
            this.targetUserId,
            this.noteType.id,
            this.$router,
          );
        } else if (this.targetUserId && !this.filterTargetUserByNoteType) {
          notes = await notesAPIService.getByTargetUserId(
            this.targetUserId,
            this.$router,
          );
        } else if (this.targetTaskId) {
          notes = await notesAPIService.getByTargetTaskId(
            this.targetTaskId,
            this.$router,
          );
        } else if (this.targetEventId) {
          notes = await notesAPIService.getByTargetEventAndNoteTypeIds(
            this.targetEventId,
            this.noteType.id,
            this.$router,
          );
        }
        this.editableNotes = notes.map((note) => ({
          ...note,
          originalNote: note.note,
          formattedDate: dayjs(note.updatedAt).format("MM/DD/YYYY"),
        }));
      } catch (error) {}
    },
    async fetchNoteTypes() {
      try {
        this.noteTypeOptions = await notesAPIService.getNoteTypeOptions();
        this.noteType = this.noteTypeOptions.find(
          (note) => note.name === this.noteTypeName,
        );
      } catch (error) {}
    },
    addNote() {
      this.isCreatingNewNote = true;
      this.editableNotes.unshift({
        id: null,
        noteSubject: "",
        note: "",
        isNew: true,
        targetUserId: this.targetUserId,
        targetTaskId: this.targetTaskId,
        targetEventId: this.targetEventId,
        createdByUserId: this.currentUser.id,
        noteType: this.noteType,
      });
      this.$nextTick(() => {
        const newNoteTextarea = this.$refs.newNote?.[0];
        if (newNoteTextarea) {
          newNoteTextarea.focus();
        }
      });
    },
    triggerFormSubmit() {
      const form = this.$el.querySelector("form");
      if (form) {
        form.requestSubmit();
      }
    },
    async saveNewNote() {
      const newNote = {
        noteSubject: this.newSubject,
        note: this.newNote,
        isNew: true,
        targetUserId: this.targetUserId,
        targetTaskId: this.targetTaskId,
        targetEventId: this.targetEventId,
        createdByUserId: this.currentUser.id,
        noteType: this.noteType,
      };

      if (!newNote.noteSubject.trim() || !newNote.note.trim()) return;

      try {
        await notesAPIService.create(
          {
            noteSubject: newNote.noteSubject,
            note: newNote.note,
            targetUserId: newNote.targetUserId,
            targetTaskId: newNote.targetTaskId,
            targetEventId: newNote.targetEventId,
            createdByUserId: this.currentUser.id,
            noteTypeId: this.noteType?.id,
          },
          this.$router,
        );

        await this.fetchNotes();
        this.isCreatingNewNote = false;
        this.newSubject = null;
        this.newNote = null;
      } catch (error) {}
    },
    closeModal() {
      this.$emit("close");
    },
    checkIfDisabled() {
      this.saveDisabled = !this.newSubject || !this.newNote;
    },
  },
};
</script>
<style module scoped>
input[type="text"] {
  width: 100%;
  max-width: 1000px;
  padding: 10px;
  font-size: 18px;
  color: #333;
  border: none;
  border-radius: 10px;
  background-color: rgba(64, 64, 64, 0.05);
}

input[type="text"]:invalid::-webkit-datetime-edit {
  color: #9b9b9b;
}
.allNotesHeader {
  position: relative;
  font-size: 25px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  z-index: 0;
}
.noteContainer {
  max-height: 25vh;
  width: 100%;
  overflow-y: auto;
}
.noteHeader {
  position: relative;
}
.dateLine {
  position: relative;
  font-size: 15px;
  color: rgba(64, 64, 64, 0.5);
}
.noteContent {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  z-index: 1;
}
.notesChild {
  align-self: stretch;
  position: relative;
  max-width: 100%;
  overflow: hidden;
  max-height: 100%;
  z-index: 2;
}
.noteHeaderUpGroup {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  z-index: 3;
}
.notesItem {
  align-self: stretch;
  position: relative;
  max-width: 100%;
  overflow: hidden;
  max-height: 100%;
  z-index: 4;
}
.subject {
  width: 160px;
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}
.subjectWrapper {
  width: 100%;
  flex: 1;
  border-radius: 10px;
  background-color: rgba(64, 64, 64, 0.05);
  height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 20px;
  box-sizing: border-box;
  color: rgba(64, 64, 64, 0.5);
  border: none;
}
.subjectParent {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 20px;
}
.vectorIcon {
  width: 13.3px;
  position: relative;
  height: 7.7px;
}
.selectAllThatApplyParent {
  flex: 1;
  border-radius: 10px;
  background-color: rgba(64, 64, 64, 0.05);
  height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0px 20px;
  box-sizing: border-box;
  color: rgba(64, 64, 64, 0.5);
}
.note {
  width: 160px;
  position: relative;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}
.noteInputContainer {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 10px;
  z-index: 5;
}
.save {
  position: relative;
  display: -webkit-inline-box;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.saveWrapper {
  width: 100%;
  align-self: stretch;
  border-radius: 5px;
  background-color: #3171e2;
  height: 50px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  z-index: 6;
  text-align: center;
  color: #fff;
  border: none;
}
.saveWrapper:disabled {
  opacity: 50%;
  cursor: not-allowed;
}
.icon {
  width: 12px;
  position: absolute;
  margin: 0 !important;
  top: 39px;
  right: 51px;
  height: 12px;
  z-index: 7;
  cursor: pointer;
}
.notes {
  width: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  -webkit-transform: translate(-50%, -50%);
  transform: translate(-50%, -50%);
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.15);
  border-radius: 15px;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 30px;
  box-sizing: border-box;
  gap: 20px;
  text-align: left;
  font-size: 18px;
  color: #404040;
  font-family: Helvetica, Arial, sans-serif;
}
.overlay {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 50;
}
.dividerLine {
  border: 0;
  border-top: 1px solid #ddd;
  height: 0;
  margin: 5px 0;
  width: 100%;
  box-sizing: border-box;
}
</style>
